import { sanitizeJsonText, sanitizeText } from '~/modules/utilities/html_utilities';

export interface UtmParams {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    listingId?: string;
    expiration_date?: string;
}

export interface ReferralParams {
    ref?: string;
    referral?: string;
    listingId?: string;
    expiration_date?: string;
}

export const UTM_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
export const REFERRAL_PARAMS = ['ref', 'referral'];
export const UTM_SOURCE_TYPE = 'UTM';
export const REFERRAL_SOURCE_TYPE = 'source';
export const REFERRAL_CODE_PARAM = 'referral_code';
export const UTM_SOURCE = 'utm_source';
export const UTM_MEDIUM = 'utm_medium';
export const UTM_CAMPAIGN = 'utm_campaign';
export const UTM_TERM = 'utm_term';
export const UTM_CONTENT = 'utm_content';
export const EXPIRATION_DATE = 'expiration_date';
const EXPIRATION_DAYS = 90;
export const ANALYTICS_REFERRER = 'analyticsReferrer';

export function updateLocalStorage(params: UtmParams | ReferralParams): void {
    const listingId = params.listingId || '';

    if (!checkUtmParams(params) && !checkReferralParams(params)) {
        return;
    }

    const storedItem = localStorage.getItem(listingId);
    const localStorageData: Record<string, any> = storedItem ? JSON.parse(storedItem) : {};

    removeExpiredItems(localStorageData);

    if (checkUtmParams(params) && checkReferralParams(params)) {
        const utmObject = generateSourceObject(params, UTM_SOURCE_TYPE);
        const referralObject = generateSourceObject(params, REFERRAL_SOURCE_TYPE);
        localStorageData.UTM = utmObject;
        localStorageData.source = referralObject;
    } else if (checkReferralParams(params)) {
        const referralObject = generateSourceObject(params, REFERRAL_SOURCE_TYPE);
        localStorageData.source = referralObject;
    } else if (checkUtmParams(params)) {
        const utmObject = generateSourceObject(params, UTM_SOURCE_TYPE);
        localStorageData.UTM = utmObject;
    }
    localStorage.setItem(listingId, sanitizeJsonText(JSON.stringify(localStorageData)));
}

export function getUtmParamsFromLocalStorage(listingId: string): { [key: string]: string } | null {
    const utmDataString = localStorage.getItem(listingId);

    const utmData = utmDataString ? JSON.parse(utmDataString)?.UTM : null;

    if (utmData) {
        const utmParams: { [key: string]: string } = {
            [UTM_SOURCE]: utmData[UTM_SOURCE],
            [UTM_MEDIUM]: utmData[UTM_MEDIUM],
            [UTM_CAMPAIGN]: utmData[UTM_CAMPAIGN],
            [UTM_TERM]: utmData[UTM_TERM],
            [UTM_CONTENT]: utmData[UTM_CONTENT],
        };

        const filteredUtmParams = Object.fromEntries(
            Object.entries(utmParams).filter(
                ([_, value]) => value !== undefined && value !== null && value.trim() !== '',
            ),
        );

        return filteredUtmParams;
    }

    return null;
}

function removeExpiredItems(localStorageData: Record<string, any>): void {
    for (const key in localStorageData) {
        if (Object.prototype.hasOwnProperty.call(localStorageData, key) && localStorageData[key][EXPIRATION_DATE]) {
            const expirationDate = new Date(localStorageData[key][EXPIRATION_DATE]);
            const currentDate = new Date();

            if (expirationDate < currentDate) {
                // Check if the item is UTM or source
                if (key === UTM_SOURCE_TYPE || key === REFERRAL_SOURCE_TYPE) {
                    delete localStorageData[key];
                }
            }
        }
    }
}

export function checkUtmParams(params: UtmParams | ReferralParams): params is UtmParams {
    return UTM_PARAMETERS.some((param) => param in params);
}

export function checkReferralParams(params: UtmParams | ReferralParams): params is ReferralParams {
    return REFERRAL_PARAMS.some((param) => param in params);
}

function setLocalStorageParamsExpirationDate(): string {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + EXPIRATION_DAYS);

    return expirationDate.toUTCString();
}

export function generateSourceObject(params: UtmParams | ReferralParams, source: string): Record<string, string> {
    const sanitizedParams = Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, sanitizeText(value || '')]),
    );
    if (source === UTM_SOURCE_TYPE) {
        return {
            [UTM_SOURCE]: sanitizedParams[UTM_SOURCE] ?? '',
            [UTM_MEDIUM]: sanitizedParams[UTM_MEDIUM] ?? '',
            [UTM_CAMPAIGN]: sanitizedParams[UTM_CAMPAIGN] ?? '',
            [UTM_TERM]: sanitizedParams[UTM_TERM] ?? '',
            [UTM_CONTENT]: sanitizedParams[UTM_CONTENT] ?? '',
            [EXPIRATION_DATE]: setLocalStorageParamsExpirationDate(),
        };
    }

    if (source === REFERRAL_SOURCE_TYPE) {
        return {
            [REFERRAL_CODE_PARAM]: sanitizedParams.ref ?? sanitizedParams.referral ?? '',
            [EXPIRATION_DATE]: setLocalStorageParamsExpirationDate(),
        };
    }

    throw new Error(`Unsupported source type: ${source}`);
}

export function isExpired(expirationDate: string): boolean {
    const expirationTime = new Date(expirationDate).getTime();
    const currentTime = new Date().getTime();

    return expirationTime < currentTime;
}

export function storeAnalyticsReferrer() {
    localStorage.setItem(ANALYTICS_REFERRER, document.referrer);
}
